<template>
  <div class="channel-news-list">
    <!-- 骨架屏 loading -->
    <div v-if="loading && items.length === 0" class="channel-news-list__skeleton">
      <div v-for="n in 3" :key="n" class="channel-news-list__skeleton-item">
        <div class="channel-news-list__skeleton-text">
          <div class="channel-news-list__skeleton-title"></div>
          <div class="channel-news-list__skeleton-title channel-news-list__skeleton-title--short"></div>
          <div class="channel-news-list__skeleton-meta"></div>
        </div>
        <div class="channel-news-list__skeleton-image"></div>
      </div>
    </div>

    <template v-else>
      <!-- 第一条：推荐阅读（只显示标题） -->
      <div v-if="featuredItem" class="channel-news-list__featured" @click="onItemClick(featuredItem)">
        <div class="channel-news-list__badge">
          <span class="channel-news-list__badge-top">推荐</span>
          <div class="channel-news-list__badge-bottom">
            <span class="channel-news-list__badge-text">阅读</span>
          </div>
        </div>
        <span class="channel-news-list__featured-title">{{ featuredItem.title }}</span>
      </div>

      <!-- 第2~N条 -->
      <template v-for="(item, idx) in restItems">
        <!-- 分割线 -->
        <div v-if="idx > 0" :key="'line-' + idx" class="channel-news-list__divider"></div>

        <!-- 有图片：图文样式 -->
        <div v-if="item.thumbnail" :key="'thumb-' + idx" class="channel-news-list__thumb-item" @click="onItemClick(item)">
          <div class="channel-news-list__thumb-text">
            <span class="channel-news-list__thumb-title">{{ item.title }}</span>
            <span class="channel-news-list__thumb-meta">{{ item.source }}&nbsp;&nbsp;&nbsp;&nbsp;{{ item.time }}</span>
          </div>
          <img
            class="channel-news-list__thumb-image"
            referrerpolicy="no-referrer"
            :src="item.thumbnail"
            alt="新闻配图"
          />
        </div>

        <!-- 无图片：纯标题样式 -->
        <div v-else :key="'text-' + idx" class="channel-news-list__text-item" @click="onItemClick(item)">
          <span class="channel-news-list__text-title">{{ item.title }}</span>
          <span class="channel-news-list__text-meta">{{ item.source.split('|')[0] }}&nbsp;&nbsp;&nbsp;&nbsp;{{ item.time }}</span>
        </div>
      </template>

      <!-- 加载更多 -->
      <div v-if="items.length > 0 && !finished" class="channel-news-list__load-more" @click="onLoadMore">
        <span v-if="!loading">加载更多</span>
        <span v-else class="channel-news-list__loading-text">加载中...</span>
      </div>

      <!-- 没有更多了 -->
      <div v-if="items.length > 0 && finished" class="channel-news-list__finished">
        没有更多新闻了！
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ChannelNewsList',
  props: {
    items: {
      type: Array,
      default: () => []
      // 每项: { title, source, time, thumbnail(可选), newsCode }
    },
    loading: {
      type: Boolean,
      default: false
    },
    finished: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    featuredItem() {
      return this.items.length > 0 ? this.items[0] : null;
    },
    restItems() {
      return this.items.slice(1);
    }
  },
  methods: {
    onLoadMore() {
      if (this.loading || this.finished) return;
      this.$emit('load-more');
    },
    onItemClick(item) {
      if (!item.newsCode && !item.id) return;
      this.$emit('item-click', item);
    }
  }
};
</script>

<style scoped>
.channel-news-list {
  width: var(--content-width);
}

/* ══════ 骨架屏 ══════ */
.channel-news-list__skeleton {
  width: 100%;
}

.channel-news-list__skeleton-item {
  display: flex;
  justify-content: space-between;
  width: var(--content-width);
  height: 160px;
  margin-bottom: 20px;
}

.channel-news-list__skeleton-text {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 475px;
  height: 160px;
}

.channel-news-list__skeleton-title {
  width: 100%;
  height: 30px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: channel-skeleton-shimmer 1.5s infinite;
}

.channel-news-list__skeleton-title--short {
  width: 70%;
}

.channel-news-list__skeleton-meta {
  width: 160px;
  height: 18px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: channel-skeleton-shimmer 1.5s infinite;
  margin-bottom: 10px;
}

.channel-news-list__skeleton-image {
  width: 272px;
  height: 153px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: var(--radius-sm);
  animation: channel-skeleton-shimmer 1.5s infinite;
  flex-shrink: 0;
}

@keyframes channel-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ══════ 推荐阅读（第一条） ══════ */
.channel-news-list__featured {
  display: flex;
  align-items: center;
  background-color: var(--color-bg-card);
  border-radius: var(--radius-lg);
  width: var(--content-width);
  height: 120px;
  margin-bottom: 28px;
  cursor: pointer;
}

.channel-news-list__badge {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 62px;
  height: 72px;
  background-color: var(--color-white);
  border: 1px solid var(--color-accent-red);
  border-radius: var(--radius-md);
  margin-left: 27px;
  flex-shrink: 0;
}

.channel-news-list__badge-top {
  display: block;
  width: 57px;
  height: 32px;
  color: var(--color-black);
  font-size: var(--font-lg);
  font-family: var(--font-primary);
  text-align: center;
  white-space: nowrap;
  line-height: 32px;
  margin: 3px auto 0;
}

.channel-news-list__badge-bottom {
  background-color: var(--color-accent-red);
  border-radius: var(--radius-md);
  height: 34px;
  width: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3px;
}

.channel-news-list__badge-text {
  color: var(--color-white);
  font-size: var(--font-lg);
  font-family: var(--font-primary);
  white-space: nowrap;
  line-height: 32px;
}

.channel-news-list__featured-title {
  display: block;
  flex: 1;
  color: var(--color-text-gray);
  font-size: var(--font-2xl);
  font-family: var(--font-primary);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 30px;
  margin-left: 36px;
}

/* ══════ 分割线 ══════ */
.channel-news-list__divider {
  width: var(--content-width);
  height: 1px;
  background-color: #EBEBEB;
  margin: 20px 0;
}

/* ══════ 有图片的条目（图文样式） ══════ */
.channel-news-list__thumb-item {
  display: flex;
  justify-content: space-between;
  width: var(--content-width);
  height: 160px;
  cursor: pointer;
}

.channel-news-list__thumb-text {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 475px;
  height: 160px;
}

.channel-news-list__thumb-title {
  display: block;
  color: var(--color-text-dark);
  font-size: var(--font-2xl);
  font-family: var(--font-primary);
  text-align: left;
  line-height: 50px;
}

.channel-news-list__thumb-meta {
  display: block;
  color: var(--color-text-light);
  font-size: var(--font-base);
  font-family: var(--font-primary);
  white-space: nowrap;
  line-height: 18px;
  margin-bottom: 10px;
}

.channel-news-list__thumb-image {
  width: 272px;
  height: 153px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

/* ══════ 无图片的条目（纯标题样式） ══════ */
.channel-news-list__text-item {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: var(--content-width);
  height: 77px;
  cursor: pointer;
}

.channel-news-list__text-title {
  display: block;
  width: var(--content-width);
  color: var(--color-black);
  font-size: var(--font-2xl);
  font-family: var(--font-primary);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 30px;
}

.channel-news-list__text-meta {
  display: block;
  color: var(--color-text-light);
  font-size: var(--font-base);
  font-family: var(--font-primary);
  white-space: nowrap;
  line-height: 18px;
  margin-top: 9px;
}

/* ══════ 加载更多 ══════ */
.channel-news-list__load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--content-width);
  height: 80px;
  margin-top: 20px;
  color: var(--color-text-light);
  font-size: var(--font-base);
  font-family: var(--font-primary);
  cursor: pointer;
  user-select: none;
}

.channel-news-list__load-more:active {
  opacity: 0.7;
}

.channel-news-list__loading-text {
  color: #999;
}

/* ══════ 没有更多了 ══════ */
.channel-news-list__finished {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--content-width);
  height: 80px;
  margin-top: 20px;
  color: var(--color-text-light);
  font-size: var(--font-base);
  font-family: var(--font-primary);
}
</style>
